<template>
  <div :class="{ 'opacity-50': disabled }">
    <div
        v-if="previewSrc"
        class="relative w-full overflow-hidden rounded border border-dashed border-primary"
        :style="containerStyle"
    >
      <img
          class="absolute inset-0 w-full h-full object-cover"
          :src="previewSrc"
          :alt="alt"
      />

      <button
          v-if="!disabled"
          class="absolute inset-0 flex items-center justify-center bg-zinc-800/75 opacity-0 hover:opacity-100 transition-opacity"
          type="button"
          :aria-label="__('Delete')"
          @click="handleDelete"
      >
        <Icon class="text-xl text-white" name="delete" />
      </button>
    </div>

    <div v-else>
      <label
          :for="disabled ? '' : `upload-${uniqueId}`"
          :class="[
          isDragging && !disabled ? 'bg-primary/5' : 'bg-zinc-50',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          `flex ${previewHeightClass} w-full mx-auto flex-col items-center justify-center rounded border border-dashed border-primary p-4 transition-colors`
        ]"
          @drop.prevent="disabled ? null : handleDrop"
          @dragover.prevent="disabled ? null : (isDragging = true)"
          @dragleave.prevent="disabled ? null : (isDragging = false)"
      >
        <Icon class="text-2xl text-primary" name="backup" is-filled />
        <span class="mt-1 text-sm font-medium text-primary">{{ alt }}</span>
        <span class="mt-2 text-xs text-zinc-600">
          {{ disabled ? __('Upload disabled') : __('Click to upload or drag & drop') }}
        </span>
        <span v-if="!disabled" class="mt-1 text-xs text-zinc-600">
          {{ `JPG, JPEG ${__('or')} PNG (max. ${maxFileSize} MB)` }}
        </span>
      </label>

      <input
          :id="`upload-${uniqueId}`"
          type="file"
          class="sr-only"
          accept="image/jpeg,image/png"
          :disabled="disabled"
          @change="handleInputChange"
      />
    </div>

    <ImageCropper
        v-model:open="isCropperOpen"
        :value="base64"
        :min-width="cropWidth"
        :min-height="cropHeight"
        :aspect-ratio="computedAspectRatio"
        @change="handleCropperChange"
        @update:errors="handleUpdateErrors"
    />

    <div v-if="errors && errors.length" class="mt-2 space-y-1">
      <span v-for="(error, index) in errors" :key="index" class="block text-xs text-error">
        {{ error }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { __ } from '@/helpers';
import { Icon } from '@/components/block/icon';
import { ImageCropper } from '@/components/other/image-cropper';

const props = defineProps({
  modelValue: {
    type: [String, null],
    default: null
  },
  file: {
    type: [File, null],
    default: null
  },
  errors: {
    type: Array,
    default: () => []
  },
  alt: {
    type: String,
    default: () => __('Upload Image')
  },
  cropWidth: {
    type: Number,
    default: 1021
  },
  cropHeight: {
    type: Number,
    default: 245
  },
  aspectRatio: {
    type: Number,
    default: null
  },
  previewHeight: {
    type: String,
    default: 'h-40'
  },
  existingUrl: {
    type: String,
    default: null
  },
  isRandomPreview: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'update:file', 'update:errors']);

const maxFileSize = 2;
const base64 = ref('');
const isCropperOpen = ref(false);
const isDragging = ref(false);
const uniqueId = ref(Math.random().toString(36).substr(2, 9));

const computedAspectRatio = computed(() => {
  if (props.aspectRatio) return Number(props.aspectRatio);

  return props.cropHeight ? props.cropWidth / props.cropHeight : 1;
});

const aspectPercent = computed(() => {
  return (props.cropHeight / props.cropWidth) * 100;
});

const containerStyle = computed(() => {
  return {
    position: 'relative',
    paddingTop: `${aspectPercent.value}%`,
    width: '100%',
    overflow: 'hidden'
  };
});

const previewSrc = computed(() => {
  const value = props.modelValue;
  if (isBase64(value)) return value;
  if (!value && props.existingUrl) return props.existingUrl;
  if (!value) return null;

  if (String(value).includes('object-') || props.isRandomPreview) {
    return null;
  }

  return `/uploads/${value}`;
});

const previewHeightClass = computed(() => {
  return props.previewHeight || 'h-40';
});

function readFiles(files) {
  if (!files || !files.length) return;

  const file = files[0];
  const validTypes = ['image/jpeg', 'image/png'];

  if (!validTypes.includes(file.type)) {
    emit('update:errors', [__('Please upload a JPG or PNG image')]);
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    base64.value = reader.result;
    isCropperOpen.value = true;
  };
  reader.readAsDataURL(file);
}

function handleInputChange(event) {
  if (props.disabled) return;
  readFiles(event.target.files);
  event.target.value = '';
}

function handleDrop(event) {
  if (props.disabled) return;
  isDragging.value = false;
  const files = event.dataTransfer.files;
  readFiles(files);
}

function handleCropperChange(croppedBase64, croppedFile) {
  if (croppedFile && croppedFile.size > maxFileSize * 1024 * 1024) {
    emit('update:errors', [
      `${__('File size exceeds limit. Please upload a file smaller than')} ${maxFileSize} MB.`
    ]);
    return;
  }

  emit('update:modelValue', croppedBase64);
  emit('update:file', croppedFile);
  emit('update:errors', []);
  isCropperOpen.value = false;
}

function handleUpdateErrors(value) {
  emit('update:errors', value);
}

function handleDelete() {
  if (props.disabled) return;
  emit('update:modelValue', null);
  emit('update:file', null);
  emit('update:errors', []);
}

function isBase64(str) {
  if (!str) return false;
  const regex = /^data:image\/[a-zA-Z]+;base64,/;
  return regex.test(str);
}
</script>
